/**
 * AssignWorkOrderModal - assign (or reassign) a work order from wherever it
 * is listed, without opening the whole edit form.
 *
 * Atomic Design role: Organism. Hosts the same AssignmentSection molecule
 * the create/edit form renders — who is on the job, whether hours are
 * required and how many, priority, the scheduled date, start/end times and
 * their timezone — inside the shared Modal, and owns the state, validation
 * and PATCH for just that section. Putting a crew on a work order after it
 * exists is most of a dispatcher's day, and the old route (Actions → Edit →
 * scroll past Project Information, Customer and Contact → Save the whole
 * form) was the wrong size for it.
 *
 * What it does, in order:
 *	 1. Fetches a fresh copy of the record on every open. The list row it
 *		was opened from can be up to 30s stale, and the crew is exactly
 *		the field another dispatcher may have just changed. The form seeds
 *		once per open, from that fetch, and never re-seeds underneath an
 *		edit in progress.
 *	 2. Validates the way the edit form does: an assignee is required, and
 *		an hour-tracked job needs hours on everyone plus a bucket on T&M.
 *		Two extra rules the edit form leaves to the server: an end time
 *		needs a start time, and must not come before it.
 *	 3. Asks the admin who the save will email (useNotifyPrompt), the same
 *		gate the edit form runs — a reassignment is the one edit that
 *		reliably sends mail.
 *	 4. PATCHes only the assignment fields. additional_assignee_ids goes
 *		out whole, even empty, because the server syncs the crew off it;
 *		the schedule fields go out as explicit nulls when cleared, because
 *		the PATCH validator ignores absent keys and the dialog shows the
 *		stored values, so what is sent must be what is on screen.
 *	 5. Invalidates the job caches (list, detail, dashboard, calendar) and
 *		closes.
 *
 * Closing with unsaved edits asks "Discard changes?" first (useDiscardGuard),
 * as the edit modal does.
 *
 * The pickers offer "+ Quick Add User" exactly as the edit form's do: this
 * organism passes AssignmentSection the same `quickAdd` option, pre-set to the
 * work order's customer, and the section hands it to whichever picker is
 * showing — beside Add Assignee and at the foot of each dropdown, or under the
 * hours rows on an hour-tracked job. People added land straight in the crew.
 * The one Quick Add modal is mounted app-wide by QuickAddUsersProvider and
 * layers above this one.
 *
 * Who may open it is the caller's call — the Work Orders list offers it to
 * anyone with the Can Reassign Work Orders permission, which the server also
 * requires to move a job to a different primary assignee.
 *
 * @example
 * <AssignWorkOrderModal isOpen={target !== null} workOrder={target} onClose={() => setTarget(null)} />
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../Modal/Modal';
import { Alert, Button, Spinner } from '../../atoms';
import { AssignmentSection } from '../../molecules/AssignmentSection';
import { useAuth } from '../../../hooks/useAuth';
import { useNotifyPrompt } from '../../../hooks/useNotifyPrompt';
import { useDiscardGuard } from '../../../hooks/useDiscardGuard';
import { workOrderService, normalizeWorkOrder } from '../../../services/workOrderService';
import type { WorkOrder, UpdateWorkOrderData } from '../../../services/workOrderService';
import { camelizeKeys } from '../../../services/api';
import { qk } from '../../../lib/queryKeys';
import { invalidateJobCaches } from '../../../lib/invalidateJobCaches';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import { readAdditionalAssigneeIds, readAssigneeHours, sameHours, sameIdSet } from '../../../utils/assigneeHours';
import { localToUtcIso, utcIsoToLocalTime } from '../../../utils/scheduleTime';
import { JOB_FORM_STYLING } from '../../../utils/formClasses';
import { T_AND_M_SUB_TYPE } from '../../../utils/constants';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface AssignWorkOrderModalProps {
	/** Controls visibility. The form re-seeds from a fresh fetch on every open. */
	isOpen: boolean;
	/** The work order to assign — the list row is enough; only its id, number
	 *  and name are read directly, everything else comes from the fetch. */
	workOrder: WorkOrder | null;
	/** Dismiss. Called at once when clean, after "Discard" when not. */
	onClose: () => void;
	/** Fired after a successful save with the record the server returned. */
	onSaved?: (workOrder: WorkOrder) => void;
}

/**
 * Everything the dialog edits, as one object, so it can be seeded, diffed
 * against its baseline and reset as a unit. Same fields, same string
 * buffering, as the corresponding slice of JobForm's state.
 */
interface AssignmentValues {
	assigneeId: string;
	additionalAssigneeIds: string[];
	priority: string;
	/** YYYY-MM-DD */
	scheduledDate: string;
	/** Local HH:MM in `timezone`; '' when unset. */
	startTime: string;
	endTime: string;
	/** IANA identifier the times are read and written in. */
	timezone: string;
	requiresHours: boolean;
	/** The hour bucket, buffered as a string so the input can sit empty. */
	hoursBucket: string;
	/** Per-person hours keyed by user id, string-buffered the same way. */
	assigneeHours: Record<string, string>;
}

/** What drives the hours rules for the record being edited. */
interface HoursContext {
	tracksHours: boolean;
	isTimeAndMaterials: boolean;
	hoursBucketOwnedByProject: boolean;
}

/* ============================================
 * HELPERS
 * ============================================ */

const FORM_ID = 'assign-work-order-form';

const EMPTY_VALUES: AssignmentValues = {
	assigneeId: '',
	additionalAssigneeIds: [],
	priority: 'medium',
	scheduledDate: '',
	startTime: '',
	endTime: '',
	timezone: 'America/New_York',
	requiresHours: false,
	hoursBucket: '',
	assigneeHours: {},
};

/** Read the dialog's starting values off a freshly fetched record. */
function readAssignmentValues(workOrder: WorkOrder, timezone: string): AssignmentValues {
	return {
		assigneeId: workOrder.assigneeId != null ? String(workOrder.assigneeId) : '',
		additionalAssigneeIds: readAdditionalAssigneeIds(workOrder),
		priority: workOrder.priority || 'medium',
		/* Eloquent date casts can arrive as a bare date or a full ISO
		   datetime; the date input wants YYYY-MM-DD. */
		scheduledDate: workOrder.scheduledDate ? String(workOrder.scheduledDate).split('T')[0] : '',
		startTime: utcIsoToLocalTime(workOrder.startTime, timezone),
		endTime: utcIsoToLocalTime(workOrder.endTime, timezone),
		timezone,
		/* normalizeWorkOrder already folds the T&M force-on into this flag. */
		requiresHours: Boolean(workOrder.requiresHours),
		hoursBucket: workOrder.allocatedHours != null && workOrder.allocatedHours !== '' ? String(workOrder.allocatedHours) : '',
		assigneeHours: readAssigneeHours(workOrder),
	};
}

/** "Did anything change?" — the crew is a set, hours compare numerically. */
function sameAssignment(a: AssignmentValues, b: AssignmentValues): boolean {
	return a.assigneeId === b.assigneeId
		&& sameIdSet(a.additionalAssigneeIds, b.additionalAssigneeIds)
		&& a.priority === b.priority
		&& a.scheduledDate === b.scheduledDate
		&& a.startTime === b.startTime
		&& a.endTime === b.endTime
		&& a.timezone === b.timezone
		&& a.requiresHours === b.requiresHours
		&& a.hoursBucket.trim() === b.hoursBucket.trim()
		&& sameHours(a.assigneeHours, b.assigneeHours);
}

/**
 * Client-side validation. The assignee + hours rules are the edit form's,
 * word for word, so the same problem reads the same on both surfaces; the
 * two time rules exist because the server refuses an end time without a
 * start time (after_or_equal has nothing to compare to) and the message it
 * sends back names a field the user can't see.
 */
function validateAssignment(values: AssignmentValues, ctx: HoursContext): Record<string, string> {
	const errors: Record<string, string> = {};

	if (!values.assigneeId) errors.assigneeId = 'Assignee is required';

	if (ctx.tracksHours) {
		const crew = [values.assigneeId, ...values.additionalAssigneeIds].filter(Boolean);
		if (crew.length === 0) {
			errors.assigneeId = 'Assign at least one person and record their hours';
		} else {
			const missing = crew.filter((uid) => {
				const value = values.assigneeHours[uid];
				return value == null || value === '' || !(Number(value) > 0);
			});
			if (missing.length > 0) {
				errors.assigneeId = `Record hours for every assignee — ${missing.length} still missing`;
			}
		}
		if (ctx.isTimeAndMaterials && !ctx.hoursBucketOwnedByProject && !(Number(values.hoursBucket) > 0)) {
			errors.hoursBucket = 'Enter the bucket of hours for this T&M work order';
		}
	}

	if (values.endTime && !values.startTime) {
		errors.endTime = 'Enter a start time before an end time';
	} else if (values.startTime && values.endTime && values.endTime < values.startTime) {
		errors.endTime = 'End time must be after the start time';
	}

	return errors;
}

/**
 * The PATCH body. Only the assignment fields, and every one of them is
 * sent — this dialog shows the stored values, so what leaves it is what is
 * on screen. Cleared date/time fields go out as explicit nulls; the PATCH
 * validator ignores absent keys, so omitting them would silently keep the
 * old schedule.
 */
function buildAssignmentPayload(values: AssignmentValues, ctx: HoursContext): UpdateWorkOrderData {
	/* Times need a date to become an instant. The scheduled date when set,
	   else today — same fallback the edit form uses. */
	const dateForTime = values.scheduledDate || new Date().toISOString().split('T')[0];

	const payload: UpdateWorkOrderData = {
		assigneeId: values.assigneeId,
		/* Whole, even empty: the server syncs the junction table off this
		   key, and leaving it out would keep whoever was removed. */
		additionalAssigneeIds: values.additionalAssigneeIds,
		priority: values.priority as UpdateWorkOrderData['priority'],
		scheduledDate: values.scheduledDate || null,
		startTime: values.startTime ? localToUtcIso(dateForTime, values.startTime, values.timezone) : null,
		endTime: values.endTime ? localToUtcIso(dateForTime, values.endTime, values.timezone) : null,
		/* Always sent, so unticking the box actually clears the flag. */
		requiresHours: ctx.tracksHours,
	};

	if (ctx.tracksHours) {
		/* The bucket shares its column with a project child's slice of the
		   parent's pool; when the project owns it, leave it alone. */
		if (!ctx.hoursBucketOwnedByProject) {
			payload.allocatedHours = values.hoursBucket.trim() !== '' ? Number(values.hoursBucket) : null;
		}
		const entries = Object.entries(values.assigneeHours).filter(([, v]) => v !== '' && v != null);
		if (entries.length > 0) {
			payload.assigneeHours = Object.fromEntries(entries.map(([uid, v]) => [uid, Number(v)]));
		}
	}

	return payload;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const AssignWorkOrderModal: React.FC<AssignWorkOrderModalProps> = ({ isOpen, workOrder, onClose, onSaved }) => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const { requestNotifyDecision, notifyPrompt } = useNotifyPrompt({ lead: 'Saving this assignment will email' });

	const userTimezone = user?.timezone || 'America/New_York';
	const workOrderId = workOrder ? String(workOrder.id) : '';

	/* ---- A fresh copy of the record, on every open ----
	 * Same key and shape as the detail page's read, so a save here lands the
	 * detail page warm. staleTime 0 so opening always refetches, whatever
	 * the cache holds — the crew is the field most likely to have moved. */
	const recordQuery = useQuery({
		queryKey: qk.workOrders.detail(workOrderId),
		enabled: isOpen && workOrderId !== '',
		staleTime: 0,
		queryFn: async (): Promise<WorkOrder> => {
			const response = await workOrderService.getById(workOrderId);
			const raw = (response.data as any)?.data ?? response.data;
			return normalizeWorkOrder(camelizeKeys(raw) as Record<string, unknown>);
		},
	});
	const record: WorkOrder | null = recordQuery.data ?? null;

	/* ---- Form state ---- */
	const [values, setValues] = useState<AssignmentValues>(EMPTY_VALUES);
	const [baseline, setBaseline] = useState<AssignmentValues>(EMPTY_VALUES);
	/** Which record the form currently holds — null until seeded for this open. */
	const [seededId, setSeededId] = useState<string | null>(null);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const errorRef = useRef<HTMLDivElement>(null);

	/* Seed once per open, from the fetch — not from cached data still being
	   refreshed, and not from a fetch that failed. Never again while open,
	   so a background refetch can't overwrite an edit in progress. */
	useEffect(() => {
		if (!isOpen) {
			setSeededId(null);
			return;
		}
		if (seededId === workOrderId) return;
		if (!record || recordQuery.isFetching || recordQuery.isError) return;
		if (String(record.id) !== workOrderId) return;

		const seed = readAssignmentValues(record, userTimezone);
		setValues(seed);
		setBaseline(seed);
		setErrors({});
		setSubmitError(null);
		setSeededId(workOrderId);
	}, [isOpen, seededId, workOrderId, record, recordQuery.isFetching, recordQuery.isError, userTimezone]);

	/* A submit error can land below the fold of the scrollable body. */
	useEffect(() => {
		const el = errorRef.current;
		if (submitError && el && typeof el.scrollIntoView === 'function') {
			el.scrollIntoView({ block: 'nearest' });
		}
	}, [submitError]);

	const isReady = seededId === workOrderId && record !== null;

	/* ---- Derived: hours rules for this record ---- */
	const isTimeAndMaterials = record?.subType === T_AND_M_SUB_TYPE;
	const tracksHours = values.requiresHours || isTimeAndMaterials;
	const hoursBucketOwnedByProject = Boolean(record?.projectWorkOrderId);
	const hoursContext: HoursContext = { tracksHours, isTimeAndMaterials, hoursBucketOwnedByProject };
	/** Numeric bucket for the hours editor's "x / y hrs" readout. */
	const hoursBucketValue = hoursBucketOwnedByProject
		? (Number(record?.allocatedHours) > 0 ? Number(record?.allocatedHours) : undefined)
		: (Number(values.hoursBucket) > 0 ? Number(values.hoursBucket) : undefined);

	const isDirty = isReady && !sameAssignment(values, baseline);

	/* ---- Closing ---- */
	const { requestClose, discardPrompt } = useDiscardGuard({ isDirty, onClose });
	const handleRequestClose = useCallback(() => {
		/* Escape and backdrop clicks still fire mid-save; a half-finished
		   PATCH with its dialog gone would be the worst of both. */
		if (isSaving) return;
		requestClose();
	}, [isSaving, requestClose]);

	const patch = useCallback((change: Partial<AssignmentValues>) => {
		setValues((prev) => ({ ...prev, ...change }));
	}, []);

	/* ---- Save ---- */
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!record) return;

		const validationErrors = validateAssignment(values, hoursContext);
		setErrors(validationErrors);
		const errorCount = Object.keys(validationErrors).length;
		if (errorCount > 0) {
			setSubmitError(errorCount === 1
				? 'Please fix the highlighted error before saving.'
				: `Please fix the ${errorCount} highlighted errors before saving.`);
			return;
		}
		setSubmitError(null);
		setIsSaving(true);

		try {
			/* Who does this email? Asked before anything is written, so an
			   admin who backs out here leaves nothing behind. */
			const notifyDecision = await requestNotifyDecision({
				kind: 'work_order',
				id: record.id,
				companyId: record.companyId || null,
				assigneeId: values.assigneeId || null,
				additionalAssigneeIds: values.additionalAssigneeIds,
			});
			if (!notifyDecision) return;

			const response = await workOrderService.update(String(record.id), {
				...buildAssignmentPayload(values, hoursContext),
				...notifyDecision,
			});
			const raw = (response.data as any)?.data ?? response.data;
			const updated = normalizeWorkOrder(camelizeKeys(raw) as Record<string, unknown>);

			/* The detail read gets the returned record straight away; every
			   other surface that shows this job refetches. */
			queryClient.setQueryData(qk.workOrders.detail(String(record.id)), updated);
			invalidateJobCaches(queryClient, 'work_order', { projectWorkOrderId: record.projectWorkOrderId ?? null });

			/* Clean before closing, so the discard guard has nothing to ask. */
			setBaseline(values);
			onSaved?.(updated);
			onClose();
		} catch (err) {
			setSubmitError(apiErrorMessage(err, 'Could not save the assignment. Check your connection and try again.'));
		} finally {
			setIsSaving(false);
		}
	};

	/* ---- Header context: which record this is ---- */
	const reference = workOrder ? `WO-${String(workOrder.id).slice(0, 8)}` : '';
	const contextParts = workOrder
		? [workOrder.projectName, workOrder.locationName || workOrder.projectAddress].filter(Boolean)
		: [];

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={handleRequestClose}
				title="Assign Work Order"
				size="lg"
				className="assign-work-order-modal"
				dataRef="organism-assign-work-order-modal"
				/* Pinned outside the scrollable body, full-width on phones so
				   the primary action is a thumb-sized target at the bottom of
				   the screen. */
				footer={(
					<div className="assign-work-order-modal__actions flex flex-col-reverse tablet:flex-row tablet:justify-end gap-8px">
						<Button type="button" variant="secondary" size="sm" onClick={handleRequestClose} disabled={isSaving} className="w-full tablet:w-auto">
							Cancel
						</Button>
						<Button type="submit" form={FORM_ID} variant="primary" size="sm" isLoading={isSaving} disabled={!isReady || !isDirty} className="w-full tablet:w-auto">
							Save Assignment
						</Button>
					</div>
				)}
			>
				{/* Which record — the dialog opens off a list, so say so. */}
				{workOrder && (
					<p className="assign-work-order-modal__context mb-16px text-13px font-sans text-brand-text-muted break-words">
						<span className="font-semibold text-brand-text">{reference}</span>
						{contextParts.map((part) => (
							<React.Fragment key={part}> · {part}</React.Fragment>
						))}
					</p>
				)}

				{submitError && (
					<div ref={errorRef}>
						<Alert variant="error" className="mb-12px">{submitError}</Alert>
					</div>
				)}

				{recordQuery.isError && !isReady ? (
					<Alert
						variant="error"
						action={
							<button type="button" onClick={() => recordQuery.refetch()} className="min-h-44px text-14px font-medium text-brand-primary hover:text-brand-primary-hover transition-colors">Try Again</button>
						}
					>
						Couldn't load this work order. Check your connection and try again.
					</Alert>
				) : !isReady ? (
					<div className="assign-work-order-modal__loading flex items-center justify-center py-32px">
						<Spinner size="md" label="Loading work order" />
					</div>
				) : (
					/* The footer's Save is wired to this form by id, so it can
					   sit outside the scroll area and still submit it. */
					<form id={FORM_ID} onSubmit={handleSubmit} className="assign-work-order-modal__form">
						<AssignmentSection
							frame="none"
							formType="work_order"
							assigneeId={values.assigneeId}
							onAssigneeChange={(id) => patch({ assigneeId: id })}
							additionalAssigneeIds={values.additionalAssigneeIds}
							onAdditionalAssigneesChange={(ids) => patch({ additionalAssigneeIds: ids })}
							/* Hours mode swaps the pickers for editable hour rows,
							   exactly as on the edit form. */
							tmMode={tracksHours}
							assigneeHours={values.assigneeHours}
							onAssigneeHoursChange={(hours) => patch({ assigneeHours: hours })}
							hoursBucketValue={hoursBucketValue}
							isTimeAndMaterials={isTimeAndMaterials}
							/* "+ Quick Add User" on the pickers — the same option,
							   passed the same way, as on the edit form. */
							quickAdd={{ companyId: record?.companyId || null }}
							/* The list only opens this for people with Can Reassign
							   Work Orders; the lock is the backstop if someone
							   without it ever gets here, since the server refuses
							   the move. */
							primaryLocked={!user?.canReassign}
							requireHours={{
								checked: values.requiresHours,
								onCheckedChange: (checked) => patch({ requiresHours: checked }),
								locked: isTimeAndMaterials,
								lockedNote: '(required for T&M)',
								bucketHours: values.hoursBucket,
								onBucketHoursChange: (value) => patch({ hoursBucket: value }),
								showBucket: !hoursBucketOwnedByProject,
								bucketRequired: isTimeAndMaterials,
								bucketElsewhereNote: 'This work order draws from the hours allocated on its project.',
							}}
							priority={values.priority}
							onPriorityChange={(priority) => patch({ priority })}
							scheduledDate={values.scheduledDate}
							onScheduledDateChange={(scheduledDate) => patch({ scheduledDate })}
							startTime={values.startTime}
							onStartTimeChange={(startTime) => patch({ startTime })}
							endTime={values.endTime}
							onEndTimeChange={(endTime) => patch({ endTime })}
							timezone={values.timezone}
							onTimezoneChange={(timezone) => patch({ timezone })}
							errors={errors}
							{...JOB_FORM_STYLING}
						/>
					</form>
				)}
			</Modal>

			{/* "Notify users" — null until a save would email someone and the
			    user is admin-tier. Layers above the dialog (z-70 over z-60). */}
			{notifyPrompt}
			{/* "Discard changes?" — same layer. */}
			{discardPrompt}
		</>
	);
};

export default AssignWorkOrderModal;
